import { Button } from "@fluentui/react-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CancelImageDraftDialog } from "./CancelImageDraftDialog";
import { useDeleteDraft } from "../../hooks/useImages";
import { ClientRoutes } from "../../models/ClientRoutes";

interface CancelImageDraftButtonProps {
  versionId: string;
  imageId?: string;
  requiresSecondConfirmation?: boolean;
  checkedOutToName?: string;
}

export const CancelImageDraftButton = ({
  versionId,
  imageId,
  requiresSecondConfirmation = false,
  checkedOutToName,
}: CancelImageDraftButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [secondDialogOpen, setSecondDialogOpen] = useState(false);

  const { mutateAsync: deleteDraft, isPending } = useDeleteDraft(
    versionId,
    imageId ?? versionId
  );

  const handleCancelDraft = async () => {
    try {
      await deleteDraft();
      setDialogOpen(false);
      setSecondDialogOpen(false);
      navigate(ClientRoutes.MediaActivityPage);
    } catch (error) {
      console.error("Error canceling image draft:", error);
    }
  };

  const handleConfirm = async () => {
    if (requiresSecondConfirmation) {
      setDialogOpen(false);
      setSecondDialogOpen(true);
      return;
    }

    await handleCancelDraft();
  };

  return (
    <>
      <Button appearance="secondary" onClick={() => setDialogOpen(true)}>
        {t("cancelDraft")}
      </Button>
      <CancelImageDraftDialog
        isOpen={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        isLoading={isPending}
        isWarning={requiresSecondConfirmation}
      />
      <CancelImageDraftDialog
        isOpen={secondDialogOpen}
        onCancel={() => setSecondDialogOpen(false)}
        onConfirm={handleCancelDraft}
        isLoading={isPending}
        isWarning
        title={t("cancelDraftDialogCheckedOutTitle")}
        message={t("cancelDraftDialogCheckedOutMessage", {
          user: checkedOutToName || t("unknownUser"),
        })}
      />
    </>
  );
};
